import { Component, Input, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { PortalService } from "../../services/portal-service.service";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Contracts } from "../../models/contracts";
import { ipValidator } from "../../validators/ipaddress";
import { signatureValidator } from "../../validators/signature";
import { tokenRequest, tokenResponse } from "../../models/tokens";
import { LogService } from "../../services/log.service";
import { MatDialog } from "@angular/material/dialog";
import { CristieUtils } from "../../core/utils.service";

@Component({
  standalone: false,
  selector: "app-token-request",
  templateUrl: "./token-request.component.html",
  styleUrls: ["./token-request.component.scss"],
})
export class TokenRequestComponent implements OnInit {
  @ViewChild("requestModal") requestModalRef: TemplateRef<any>;
  RequestTokensForm: UntypedFormGroup;
  @Input() contract: Contracts;
  success = false;
  error: any = null;
  requesting = false;
  tokenResponse = new Array<tokenResponse>();

  constructor(
    private fb: UntypedFormBuilder,
    private portalService: PortalService,
    private dialog: MatDialog,
    private logService: LogService,
  ) {}

  ngOnInit() {
    const siteLicensed = this.isSiteLicensed;
    this.RequestTokensForm = this.fb.group({
      agreement: 0,
      machineType: [{ value: "Physical", disabled: true }],
      tokenType: this.contract?.product === "VA" ? "Upgrade" : "Full",
      hostIp: ["", [ipValidator]],
      hostName: siteLicensed ? "site licensed" : "",
      tokenCount: [0, [Validators.required]],
      signature: [siteLicensed ? "." : "", siteLicensed ? [] : [Validators.required, signatureValidator]],
    });
    if (siteLicensed) {
      this.f.hostName.disable();
      this.f.signature.disable();
    }
  }

  tokentypeChange() {
    this.get_max();
    this.f.tokenCount.patchValue(this.isRABulkSelected ? 1 : 0);
  }

  get f() {
    return this.RequestTokensForm.controls;
  }

  get signatureLength(): number {
    return String(this.f.signature?.value || "").length;
  }

  get showSignatureCountState(): boolean {
    return !!this.f.signature && (this.f.signature.dirty || this.f.signature.touched);
  }

  get signatureCountClass(): string {
    if (!this.showSignatureCountState) return "";
    return this.signatureLength === 35 ? "signature-count-valid" : "signature-count-invalid";
  }

  get isFSBlocker(): boolean {
    return this.contract?.product === "FSBLOCKER";
  }

  get isVA(): boolean {
    return this.contract?.product === "VA";
  }

  get isSiteLicensed(): boolean {
    return this.contract != null && this.contract.siteEnabled === true;
  }

  get canRequestTokensForContract(): boolean {
    return this.contract?.product === "CLONEMANAGER" || this.isFSBlocker || this.isVA;
  }

  get tokenTargetSystemName(): string {
    return this.isFSBlocker ? "system running FSBlocker" : "Cristie Appliance";
  }

  get isRABulkSelected(): boolean {
    return this.isVA && this.f.tokenType.value === "RABulk";
  }

  requestToken() {
    this.resetMessage();
    if (this.requesting) {
      return false;
    }
    if (!this.RequestTokensForm.valid || this.contract == null) {
      return false;
    }
    this.requesting = true;
    const request = new tokenRequest();
    if (this.isFSBlocker) request.capacityTB = this.f.tokenCount.value;
    else if (this.isVA) this.setVATokenQuantity(request);
    else request.quantity = this.f.tokenCount.value;
    request.ip = this.f.hostIp.value;
    request.hostname = this.f.hostName.value;
    request.signature = this.f.signature.value;
    request.comment = `Obtained by ${CristieUtils.decodeBase64(localStorage.getItem("name"))} VIA the Licensing Portal`;
    return this.portalService.RequestToken(Array(request), this.contract.id).subscribe(
      (response) => {
        this.success = true;
        this.tokenResponse = response;
        this.decrementAvailableTokens(request);
        this.requesting = false;
        this.open(this.requestModalRef);
      },
      (error) => {
        this.logService.log("Token Request failed: ", error);
        this.error = error;
        this.requesting = false;
      },
    );
  }
  resetMessage() {
    this.success = false;
    this.error = null;
  }

  checkTokens() {
    if (!this.RequestTokensForm || !this.contract) return true;

    if (this.isSiteLicensed) return Number(this.contract.siteLicences || 0) < 1;

    if (this.isFSBlocker && this.contract.capacity) return this.contract.capacity.available < 1;

    if (this.isVA) return this.getVATokenAvailable() < 1;

    if (this.f.tokenType.value === "Full" && this.contract.cloneTokens) return this.contract.cloneTokens.available < 1;

    if (this.f.tokenType.value === "Sync" && this.contract.syncTokens) return this.contract.syncTokens.available < 1;

    return true;
  }

  get_max() {
    if (!this.RequestTokensForm || !this.contract) {
      return 0;
    }

    if (this.isSiteLicensed) {
      return Number(this.contract.siteLicences || 0);
    }

    if (this.isFSBlocker && this.contract.capacity) {
      return this.contract.capacity.available;
    }

    if (this.isVA) {
      return this.getVATokenAvailable();
    }

    if (this.f.tokenType.value === "Full" && this.contract.cloneTokens) {
      return this.contract.cloneTokens.available;
    }

    if (this.f.tokenType.value === "Sync" && this.contract.syncTokens) {
      return this.contract.syncTokens.available;
    }
    return 0;
  }

  validateTokenCount() {
    if (!this.RequestTokensForm || !this.contract) {
      return true;
    }

    const count = this.f.tokenCount.value;

    if (this.isSiteLicensed && count > 0 && count <= Number(this.contract.siteLicences || 0)) {
      return false;
    }

    if (this.isRABulkSelected) {
      return this.getVATokenAvailable() < 1;
    }

    if (this.isFSBlocker && this.contract.capacity && count > 0 && count <= this.contract.capacity.available) {
      return false;
    }

    if (this.isVA && count > 0 && count <= this.getVATokenAvailable()) {
      return false;
    }

    if (this.f.tokenType.value === "Full" && count > 0 && count <= this.contract.cloneTokens.available) {
      return false;
    }

    if (this.f.tokenType.value === "Sync" && count > 0 && count <= this.contract.syncTokens.available) {
      return false;
    }

    return true;
  }

  requestTokenDisabled(): boolean {
    return this.requesting || this.RequestTokensForm.invalid || (!this.isRABulkSelected && this.f.tokenCount.value === 0) || this.checkTokens() || this.validateTokenCount();
  }

  open(content) {
    this.dialog.open(content, { width: "1200px", maxWidth: "95vw" });
  }

  closeDialog(): void {
    this.dialog.closeAll();
  }

  copyTokenDetails(): void {
    const entries = this.tokenResponse || [];
    if (!entries.length) return;

    const lines = this.isFSBlocker
      ? [
          "HOSTNAME\tACTIVATION CODE\tCAPACITY TOKEN",
          ...entries.map((token) => `${token.hostname || ""}\t${token.activationCode || ""}\t${token.tokenCode || ""}`),
        ]
      : this.isVA
        ? [
            "HOSTNAME\tACTIVATION CODE\tUPGRADE TOKEN\tSTAGING TOKEN\tRECOVERY ASSURANCE TOKEN\tRECOVERY ASSURANCE UNLIMITED TOKEN",
            ...entries.map((token) => `${token.hostname || ""}\t${token.activationCode || ""}\t${token.upgradeCode || ""}\t${token.stagingCode || ""}\t${token.raCode || ""}\t${token.raBulkCode || ""}`),
          ]
      : [
          "HOSTNAME\tCLONE CODE\tSYNC CODE",
          ...entries.map((token) => `${token.hostname || ""}\t${token.cloneCode || ""}\t${token.syncCode || ""}`),
        ];

    navigator.clipboard.writeText(lines.join("\n"));
  }

  private decrementAvailableTokens(request: tokenRequest): void {
    if (!this.contract) return;

    if (this.isSiteLicensed) {
      const amount = this.getSiteTokenRequestAmount(request);
      if (!amount) return;
      this.contract.siteLicences = Math.max(0, Number(this.contract.siteLicences || 0) - amount);
      return;
    }

    if (this.isFSBlocker && this.contract.capacity) {
      const amount = Number(request.capacityTB || 0);
      if (!amount) return;
      this.contract.capacity.available = Math.max(0, Number(this.contract.capacity.available || 0) - amount);
      return;
    }

    if (this.isVA) {
      if (this.f.tokenType.value === "RABulk") return;
      const amount = this.getVATokenRequestAmount(request);
      if (!amount) return;
      this.decrementVATokenAvailable(amount);
      return;
    }

    const amount = Number(request.quantity || 0);
    if (!amount) return;

    if (this.f.tokenType.value === "Sync" && this.contract.syncTokens) {
      this.contract.syncTokens.available = Math.max(0, Number(this.contract.syncTokens.available || 0) - amount);
      return;
    }

    if (this.contract.cloneTokens) {
      this.contract.cloneTokens.available = Math.max(0, Number(this.contract.cloneTokens.available || 0) - amount);
    }
  }

  private setVATokenQuantity(request: tokenRequest): void {
    const amount = this.f.tokenCount.value;
    switch (this.f.tokenType.value) {
      case "Upgrade":
        request.upgradeQuantity = amount;
        break;
      case "Staging":
        request.stagingQuantity = amount;
        break;
      case "RA":
        request.raQuantity = amount;
        break;
      case "RABulk":
        request.raBulkQuantity = 1;
        break;
    }
  }

  private getVATokenAvailable(): number {
    switch (this.f.tokenType.value) {
      case "Upgrade":
        return Number(this.contract?.upgradeTokens?.available || 0);
      case "Staging":
        return Number(this.contract?.stagingTokens?.available || 0);
      case "RA":
        return Number(this.contract?.raTokens?.available || 0);
      case "RABulk":
        return Number(this.contract?.raBulkTokens?.available || 0);
      default:
        return 0;
    }
  }

  private decrementVATokenAvailable(amount: number): void {
    switch (this.f.tokenType.value) {
      case "Upgrade":
        if (this.contract.upgradeTokens) this.contract.upgradeTokens.available = Math.max(0, Number(this.contract.upgradeTokens.available || 0) - amount);
        break;
      case "Staging":
        if (this.contract.stagingTokens) this.contract.stagingTokens.available = Math.max(0, Number(this.contract.stagingTokens.available || 0) - amount);
        break;
      case "RA":
        if (this.contract.raTokens) this.contract.raTokens.available = Math.max(0, Number(this.contract.raTokens.available || 0) - amount);
        break;
      case "RABulk":
        break;
    }
  }

  private getVATokenRequestAmount(request: tokenRequest): number {
    switch (this.f.tokenType.value) {
      case "Upgrade":
        return Number(request.upgradeQuantity || 0);
      case "Staging":
        return Number(request.stagingQuantity || 0);
      case "RA":
        return Number(request.raQuantity || 0);
      default:
        return 0;
    }
  }

  private getSiteTokenRequestAmount(request: tokenRequest): number {
    if (this.isFSBlocker) return Number(request.capacityTB || 0);
    if (this.isVA) return this.getVATokenRequestAmount(request);
    return Number(request.quantity || 0);
  }
}
